import { appURLs } from "@/app/constants.mjs";
import type { ValorantApiMatch } from "@/data-models/valorant-api-match-data.mjs";
import type { ValorantCoachingBenchmark } from "@/data-models/valorant-coaching-benchmark-data.mjs";
import type { ValorantCoachingData } from "@/data-models/valorant-coaching-data.mjs";
import type { ValorantContent } from "@/data-models/valorant-content.mjs";
import type { ValorantLeaderboard } from "@/data-models/valorant-leaderboard.mjs";
import type { ValorantMatchWeaponStats } from "@/data-models/valorant-match-weapon-stats.mjs";
import type { ValorantProfile } from "@/data-models/valorant-profile.mjs";
import type { ValorantAgentStats } from "@/data-models/valorant-stats-agents.mjs";
import type { ValorantMapStats } from "@/data-models/valorant-stats-maps.mjs";
import type { ValorantWeaponStats } from "@/data-models/valorant-stats-weapons.mjs";
import type { AgentInsights, Agents } from "@/game-val/models/agents.mjs";
import type { AgentInlineEntry } from "@/game-val/models/article-inline-entry.mjs";
import type { Maps } from "@/game-val/models/maps.mjs";
import type { Tips, TipsCollection } from "@/game-val/models/tips.mjs";
import type { Weapons } from "@/game-val/models/weapons.mjs";

export const RANK_IMGS = {
  radiant: {
    small: `${appURLs.CDN_PLAIN}/blitz/val/ranks/radiant_small.svg`,
  },
  immortal: {
    small: `${appURLs.CDN_PLAIN}/blitz/val/ranks/immortal_small.svg`,
  },
};

export const NEW_MAP = "Abyss";

export const GAME_MODES = [
  "overall", //all
  "competitive",
  "swiftplay",
  "premier",
  "custom",
  "spikerush",
  "deathmatch",
  "unrated",
  "snowball",
  "escalation",
  "replication",
  "Team Deathmatch",
  // "ggteam",
  // NEW_MAP,
  // "onefa",
];

export const CONSOLE_GAME_MODES = [
  "overall",
  "Console Unrated",
  "Console Swiftplay",
  "Console Deathmatch",
  "Console Team Deathmatch",
];

// TODO: This needs to be moved to CMS
export const EXCLUDED_COMPETITIVE_MAPS = [
  "fracture",
  "haven",
  "icebox",
  "lotus",
];
export const TDM_MAPS = ["piazza", "kasbah", "district", "drift", "glitch"];

export const EXCLUDED_QUEUES = [
  "custom",
  "deathmatch",
  "ggteam",
  "hurm",
  "console_deathmatch",
  "console_hurm",
];

export const STATS_GAME_MODES = [
  "overall", //all
  "competitive",
  "swiftplay",
  "premier",
  "custom",
  "spikerush",
  "unrated",
  "escalation",
  "Team Deathmatch",
];

export const GAME_VAL_RANK_COLORS = {
  unrated: {
    fill: "var(--shade6)",
    text: "var(--shade3)",
    gradiant: {
      start: "var(--shade5)",
      end: "var(--shade6)",
    },
  },
  iron: {
    fill: "#586062",
    text: "#586062",
    gradiant: {
      start: "#586062",
      end: "#586062",
    },
  },
  bronze: {
    fill: "#A78260",
    text: "#A78260",
    gradiant: {
      start: "#A78260",
      end: "#A78260",
    },
  },
  silver: {
    fill: "#BAC7C8",
    text: "#BAC7C8",
    gradiant: {
      start: "#BAC7C8",
      end: "#BAC7C8",
    },
  },
  gold: {
    fill: "#CDA353",
    text: "#CDA353",
    gradiant: {
      start: "#CDA353",
      end: "#CDA353",
    },
  },
  platinum: {
    fill: "#4CACB9",
    text: "#4CACB9",
    gradiant: {
      start: "#4CACB9",
      end: "#4CACB9",
    },
  },
  diamond: {
    fill: "#9655E7",
    text: "#9655E7",
    gradiant: {
      start: "#9655E7",
      end: "#9655E7",
    },
  },
  ascendant: {
    fill: "#6AE2AF",
    text: "#6AE2AF",
    gradiant: {
      start: "#6AE2AF",
      end: "#6AE2AF",
    },
  },
  immortal: {
    fill: "#C2264C",
    text: "#C2264C",
    gradiant: {
      start: "#C2264C",
      end: "#C2264C",
    },
  },
  radiant: {
    fill: "#E1EEEA",
    text: "#E1EEEA",
    gradiant: {
      start: "var(--white)",
      end: "var(--white)",
    },
  },
};

export const COACHING_IMAGES = {
  OverallBodyImg: `${appURLs.CDN}/blitz/val/assets/coaching/crosshair/bg_cross_overall.png`,
  ShortBodyImg: `${appURLs.CDN}/blitz/val/assets/coaching/crosshair/bg_cross_short.png`,
  MediumBodyImg: `${appURLs.CDN}/blitz/val/assets/coaching/crosshair/bg_cross_medium.png`,
  LongRangeBodyImg: `${appURLs.CDN}/blitz/val/assets/coaching/crosshair/bg_cross_long.png`,

  SprayControlBg: `${appURLs.CDN}/blitz/val/assets/coaching/crosshair/bg_overview_spray.png`,
  CrosshairPlacementBg: `${appURLs.CDN}/blitz/val/assets/coaching/crosshair/bg_overview_crosshair.png`,
  AccuracyBg: `${appURLs.CDN}/blitz/val/assets/coaching/crosshair/bg_overview_accuracy.png`,

  HelpImg: `${appURLs.CDN}/blitz/val/assets/coaching/crosshair/help.png`,

  BodyImg: `${appURLs.CDN}/blitz/val/assets/coaching/postmatch/bg_graph.png`,

  yellow_lock: `${appURLs.CDN}/blitz/val/assets/coaching/yellow_lock.png`,

  CrosshairImg: `${appURLs.CDN_PLAIN}/blitz/val/coaching/crosshair.svg`,
  SprayControlImg: `${appURLs.CDN_PLAIN}/blitz/val/coaching/spraycontrol.svg`,
  AccuracyImg: `${appURLs.CDN_PLAIN}/blitz/val/coaching/headshot.svg`,
  info: `${appURLs.CDN_PLAIN}/blitz/val/coaching/info.svg`,

  PlusImg: `${appURLs.CDN_PLAIN}/blitz/val/coaching/plus.svg`,
  MinusImg: `${appURLs.CDN_PLAIN}/blitz/val/coaching/minus.svg`,
  DividerImg: `${appURLs.CDN_PLAIN}/blitz/val/coaching/divider.svg`,

  AccuracySkillVideo: `${appURLs.CDN_VIDEOS}/valorant/coaching/accuracy.webm`,
  CrosshairSkillVideo: `${appURLs.CDN_VIDEOS}/valorant/coaching/crosshair-placement.webm`,
  SpraySkillVideo: `${appURLs.CDN_VIDEOS}/valorant/coaching/spray-control.webm`,
};

export const ESCALATION_ABILITY_CASTS = {
  ability1Casts: "shockbolt", // q
  ability2Casts: "paintshells", // e
  grenadeCasts: "boombot", // c
  ultimateCasts: "showstopper", // r
};

export const VAL_REGIONS = [
  {
    text: "NA",
    value: "na",
  },
  {
    text: "AP",
    value: "ap",
  },
  {
    text: "BR",
    value: "br",
  },
  {
    text: "EU",
    value: "eu",
  },
  {
    text: "KR",
    value: "kr",
  },
  {
    text: "LATAM",
    value: "latam",
  },
] as const;

export const TOP_REGIONS = ["na", "eu", "kr", "br", "latam", "ap"];
export const NUMBER_OF_LEADERBOARDS_ON_HOMEPAGE = 3;

export const REGIONS_MAP: Record<
  string,
  (typeof VAL_REGIONS)[number]["value"]
> = {
  EUW: "eu",
  EUNE: "eu",
  TR: "eu",
  RU: "eu",
  VN: "ap",
  BR: "br",
  LAS: "latam",
  TH: "ap",
  PH: "ap",
  SG: "ap",
  NA: "na",
  KR: "kr",
  OCE: "ap",
  TW: "ap",
  LAN: "latam",
  JP: "ap",
};

export const RANGES = [
  { text: ["val:ranges.all", "All Ranges"], value: "" },
  { text: ["val:ranges.short", "0-20m"], value: "short" },
  { text: ["val:ranges.medium", "21-40m"], value: "medium" },
  { text: ["val:ranges.long", "41m +"], value: "long" },
];

export const OVERLAY_IDS = {
  valBenchmark: "valBenchmark",
  valBenchmarkAd: "valBenchmarkAd",
  valAgentSelectP1: "valAgentSelectP1",
  valAgentSelectP2: "valAgentSelectP2",
  valAgentSelectP3: "valAgentSelectP3",
  valAgentSelectP4: "valAgentSelectP4",
  valAgentSelectP5: "valAgentSelectP5",
  valAgentSelectInfo: "valAgentSelectInfo",
  valAgentSelectAd: "valAgentSelectAd",
  valAgentSelectTopAgents: "valAgentSelectTopAgents",
  valLoadingMatchP1: "valLoadingMatchP1",
  valLoadingMatchP2: "valLoadingMatchP2",
  valLoadingMatchP3: "valLoadingMatchP3",
  valLoadingMatchP4: "valLoadingMatchP4",
  valLoadingMatchP5: "valLoadingMatchP5",
  valLoadingMatchP6: "valLoadingMatchP6",
  valLoadingMatchP7: "valLoadingMatchP7",
  valLoadingMatchP8: "valLoadingMatchP8",
  valLoadingMatchP9: "valLoadingMatchP9",
  valLoadingMatchP10: "valLoadingMatchP10",
  valLoadingMatchInfo: "valLoadingMatchInfo",
  valLoadingMatchAd: "valLoadingMatchAd",
  valLoadingMatchWinPrediction: "valLoadingMatchWinPrediction",
};

export const VALORANT_LINEUPS_PATH = "/valorant/lineups";

export const DEFAULT_MATCHES_LIMIT = 20;

export const GUIDE_PAGE_LIMIT = 16;

export const LIVE_EXPIRY_TIME = 60 * 60 * 1000; // 1 hour

export const LIVE_COMPARISON_OPTIONS = (numberOfGames = 0) => [
  {
    disabled: false,
    text: ["val:team", "Team"],
    value: "team",
  },
  {
    disabled: false,
    text: ["val:lobby", "Lobby"],
    value: "lobby",
  },
  {
    disabled: false,
    text: [
      "common:recentnAvg",
      "Recent {{numberOfGames}} Avg.",
      {
        numberOfGames,
      },
    ],
    value: "recentnAvg",
  },
];

declare module "@/__main__/app-state.mjs" {
  interface AppState {
    val: {
      playerMatches: object;
      playerMatchWinHistory: object;
      rankedRatingsForAct: object;
      profiles: Record<string, ValorantProfile | undefined>;
      content: ValorantContent;
      cms: {
        agents: Agents;
        agentInsights: Record<string, AgentInsights>;
        weapons: Weapons;
        maps: Maps;
        entryInline: Record<string, AgentInlineEntry>;
      };
      match: Record<string, ValorantApiMatch | undefined>;
      matchWeaponStats: Record<
        string,
        Record<string, ValorantMatchWeaponStats | undefined>
      >;
      leaderboard: Record<string, ValorantLeaderboard[]>;
      latestPlayerStats: object;
      playerAgentStats: object;
      playerMapStats: object;
      playerWeaponStats: object;
      playerActStats: object;
      stats: {
        agents: Record<string, ValorantAgentStats | undefined>;
        maps: Record<string, ValorantMapStats | undefined>;
        weapons: Record<string, ValorantWeaponStats | undefined>;
      };
      actSummaries: object;
      live: object;
      coachingBenchmark: ValorantCoachingBenchmark;
      coachingData: Record<string, ValorantCoachingData>;
      tips: {
        list: Record<string, TipsCollection>;
        related: {
          agent: Record<string, Tips>;
          map: Record<string, Tips>;
        };
        all: Record<string, Tips[number]>;
      };
    };
  }
}

export const PRIVATE_PROFILE_ERROR_CODES = [
  "unprocessable_entity",
  "unauthorized",
  "forbidden",
];

export const OUT_OF_ROTATION_MAPS = ["sunset", "fracture", "pearl"];

export const WEAPON_AGGREGATES_BLACKLIST = [
  "0AFB2636-4093-C63B-4EF1-1E97966E2A3E", // SPIKE

  "129B0CFB-4C77-45E5-BA86-5694B231B242", // BIG KNIFE (Escalation)
  "5261A41B-4C56-8EB5-9E46-7193F363477E", // Snowball Launcher
  "2A117B48-4BED-0701-5DCD-FCAC06047735", // Snowball Launcher

  "856D9A7E-4B06-DC37-15DC-9D809C37CB90", // Headhunter (Chamber Q)
  "39099FB5-4293-DEF4-1E09-2E9080CE7456", // Tour De Force (Chamber X)
  "9677D521-45F0-0EFF-AD21-81A03FC8A314", // KAY/O Core (Kayo X)
  "95336AE4-45D4-1032-CFAF-6BAD01910607", // Overdrive (Neon X)
  "2E20A5AF-4726-95F6-7D9C-9DB9A18DADA9", // Boom Bot (Raze C)
  "D002A9A2-4B43-86C6-1DBC-58BC7815FF9C", // Blast Pack (Raze Q)
  "6BD46791-4F27-AA65-7D67-3F8D9D8B8B90", // Paint Shells (Raze E)
  "9438BE81-46B8-D8B0-8A66-F9A0251FB728", // Showstopper (Raze X)
  "193C78AF-4498-4D51-C445-ADAC288425BE", // Shock Bolt (Sova Q)
];

export const ARMOR_TYPES = {
  heavy: "822BCAB2-40A2-324E-C137-E09195AD7692",
  light: "4DEC83D5-4902-9AB3-BED6-A7A390761157",
} as const;

export const SCORE_BREAKPOINTS = {
  bot: 0.3,
  mid: 0.5,
  top: 0.75,
} as const;
